import { filterPostType } from "assets/data/filters";
import { filterCountries } from "assets/data/incidents";
import { Filter } from "components";
import { useEffect, useState } from "react";
import { FilterSearchCheckItem, FilterSearchItem, FilterCheckItem } from "ui";
import Breadcrumb, { BreadcrumbObject } from "ui/files/core/Breadcrumb";
import { checkItem } from "ui/files/others/FilterCheckItem";

// /----------------------------------
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded"; // view
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined"; // suspend
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined"; // unsuspend
import SettingsBackupRestoreRoundedIcon from "@mui/icons-material/SettingsBackupRestoreRounded"; // restore
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"; // delete
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import { hashtags } from "assets/data/hashtags";
import { HashtagObj } from "utils/types/general.types";

const Hashtags = () => {
    const breadcrumb: BreadcrumbObject[] = [
        {
            path: "/",
            text: "Main"
        }
    ];

    const [openFilter, setOpenFilter] = useState(false);

    // filters
    const toggleItem = (item: checkItem, items: checkItem[]) => {
        const arr: checkItem[] = items.map((i) => {
            if (i.label == item.label) {
                return { label: item.label, checked: !item.checked };
            } else {
                return i;
            }
        });
        return arr;
    };

    // user name
    const [userName, setUserName] = useState("");

    // country
    const [country, setCountry] = useState("");
    const [countries, setCountries] = useState(filterCountries);
    const [filteredCountries, setFilteredCountries] = useState(filterCountries);

    const filterAllCountries = () => {
        const arr = countries.filter((c) => c.label.indexOf(country) > -1);
        setFilteredCountries(arr);
    };

    useEffect(() => {
        filterAllCountries();
    }, [country, countries]);

    const toggleCountry = (c: checkItem) => {
        const arr = toggleItem(c, countries);
        setCountries(arr);
    };

    // media type
    const [postTypes, setPostTypes] = useState(filterPostType);
    const toggleMediaType = (m: checkItem) => {
        const arr = toggleItem(m, postTypes);
        setPostTypes(arr);
    };

    // clear all filters
    const clearFilters = () => {
        setUserName("");
        setCountry("");
        setCountries(filterCountries);
        setPostTypes(filterPostType);
    };

    // hashtag table
    const [hashtagsArr, setHashtagsArr] = useState(hashtags);

    const handleSuspend = (id: string | number, state: boolean) => {
        const newArr = hashtagsArr.map((i) =>
            i.id == id ? { ...i, suspended: state } : i
        );
        setHashtagsArr(newArr);
    };
    const handleDelete = (id: string | number, state: boolean) => {
        const newArr = hashtagsArr.map((i) =>
            i.id == id ? { ...i, deleted: state } : i
        );
        setHashtagsArr(newArr);
    };

    const isSuspended = (id: string | number) => {
        return hashtagsArr.filter((i) => i.id === id)[0].suspended;
    };
    const isDeleted = (id: string | number) => {
        return hashtagsArr.filter((i) => i.id === id)[0].deleted;
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <Link
                    to={`/hashtags/view/${params.value}`}
                    className="w-full block"
                >
                    {params.value}
                </Link>
            )
        },
        { field: "count", headerName: "Count", flex: 1, minWidth: 120 },
        {
            field: "id",
            headerName: "Actions",
            renderCell: (params) => (
                <div className="flex justify-center items-center gap-3 w-full">
                    <Link
                        to={`/hashtags/view/${hashtagsArr.filter((i) => i.id === params.value)[0].name}`}
                    >
                        <VisibilityRoundedIcon
                            className="dark:text-white"
                            fontSize="small"
                        />
                    </Link>
                    {isSuspended(params.value) ? (
                        // hashtag is suspended, make unsuspend option visible
                        <div
                            onClick={() => handleSuspend(params.value, false)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="unsuspend">
                                <IconButton>
                                    <PlayCircleFilledWhiteOutlinedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        // hashtag is not suspended, make unsuspend option visible
                        <div
                            onClick={() => handleSuspend(params.value, true)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="suspend">
                                <IconButton>
                                    <PauseCircleOutlineOutlinedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}

                    {isDeleted(params.value) ? (
                        // hashtag is deleted, make restore option visible
                        <div
                            onClick={() => handleDelete(params.value, false)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="restore">
                                <IconButton>
                                    <SettingsBackupRestoreRoundedIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        // hashtag is not deleted, make delete option visible
                        <div
                            onClick={() => handleDelete(params.value, true)}
                            className="w-fit cursor-pointer"
                        >
                            <Tooltip title="delete">
                                <IconButton>
                                    <DeleteForeverIcon
                                        className="dark:text-white"
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
                </div>
            ),
            flex: 2,
            minWidth: 150,
            align: "center",
            headerAlign: "center"
        }
    ];

    return (
        <section className="w-full h-full flex flex-col relative">
            <div className="w-full text-end lg:hidden bg-neutral-100 dark:bg-momeento-300 px-4 py-2">
                <button
                    className="rounded-full bg-momeento-50 text-white py-1 px-4"
                    onClick={() => setOpenFilter(true)}
                >
                    filter By
                </button>
            </div>
            <div className="w-full h-full flex relative  overflow-auto">
                <div className="w-full flex flex-col lg:w-3/4">
                    <div className="w-full text-center">
                        <Breadcrumb
                            breadcrumb={breadcrumb}
                            currentPath="Hashtags"
                        />
                        <h1 className="font-medium text-lg dark:text-white py-8">
                            Hashtags
                        </h1>
                    </div>
                    <div className="w-full items-center flex flex-col gap-10 px-10 pb-10">
                        {/* add table here */}
                        <DataGrid
                            rows={hashtagsArr}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        page: 0,
                                        pageSize: 15
                                    }
                                },
                                sorting: {
                                    sortModel: [
                                        { field: "count", sort: "desc" }
                                    ]
                                }
                            }}
                            pageSizeOptions={[15, 30, 50, 100]}
                            getRowId={(row: HashtagObj) => row.id}
                            getRowClassName={(params) =>
                                `${params.row.suspended && "row-suspended"} ${params.row.deleted && "row-deleted"}`
                            }
                            className="w-full"
                            // checkboxSelection
                        />
                    </div>
                </div>
            </div>
            <Filter
                open={openFilter}
                setOpen={setOpenFilter}
                clear={clearFilters}
            >
                <FilterSearchCheckItem
                    title="Countries"
                    val={country}
                    setVal={setCountry}
                    toggleItem={toggleCountry}
                    items={filteredCountries}
                />
                <FilterSearchItem
                    title="User Name"
                    val={userName}
                    setVal={setUserName}
                />
                <FilterCheckItem
                    title="Post Type"
                    items={postTypes}
                    toggleItem={toggleMediaType}
                />
            </Filter>
        </section>
    );
};

export default Hashtags;
